export type AvatarSize = 'sm' | 'md';

const SIZES: Record<AvatarSize, string> = {
  sm: 'size-8 text-xs',
  md: 'size-12 text-base',
};

/**
 * The first letters of a name: two words give two, one gives one.
 *
 * An email falls back to its first letter, which is all there is before somebody
 * fills their name in.
 */
export function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '';
  if (words.length === 1) return words[0].slice(0, 1).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
}

export interface AvatarProps {
  /** The person's name, or their email when there is no name yet. */
  name: string;
  size?: AvatarSize;
  className?: string;
}

/**
 * A person, as a circle of initials.
 *
 * No photographs: nobody uploads one in a billing back office, and a broken image
 * is worse than a letter. `aria-hidden` because the name is always written next
 * to it or carried by the control's own label.
 */
export function Avatar({ name, size = 'sm', className = '' }: AvatarProps) {
  return (
    <span
      aria-hidden="true"
      className={[
        'inline-flex shrink-0 items-center justify-center rounded-full font-semibold',
        'bg-[var(--color-accent-subtle)] text-[var(--color-accent)]',
        SIZES[size],
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {initials(name)}
    </span>
  );
}
